import React, { useEffect, useRef, useState } from 'react';
import { initializePanel, initializePanelQueueCheck } from '../initialize-panel';
import CallMenu from './call/CallMenu';
import EmailMenu from './email/EmailMenu';
import EmailMenuButton from './email/EmailMenuButton';
import CallMenuButton from './call/CallMenuButton';
import ChatMenuButton from './chat/ChatMenuButton';
import CloseMenuButton from './CloseMenuButton';
import { useVisibility } from './lib/visibility';
import { IconChatBubbles } from './common/svgicons/ChatBubbles';
import { showChatButton, showCallButton, showEmailButton } from './lib/buttons';
import { IconChatWithUs } from './common/svgicons/chat-with-us';
import Svg from './common/Svg';

const IfHelpPanel = () => {
    const iimclient = (window as any).iimclient;
    const segment = iimclient.segment;
    const isNoCom = iimclient.nocom;
    const tracking = (window as any).NordicTracking;

    const [displayName, setDisplayName] = useState('');
    const [mobile, setMobile] = useState('');
    const [callMenuInitState, setCallMenuInitState] = useState(isNoCom ? Date.now() : 0);
    const [isLoading, setIsLoading] = useState(true);
    const [visibility, setActiveMenu] = useVisibility();
    const [tooltipContent, setTooltipContent] = useState('');
    const backButtonRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        initializePanel();
        initializePanelQueueCheck();
        augmentIdentitySilent();
        setIsLoading(false);
    }, []);

    // Move the focus into the panel when it becomes active. For example, when we open WCB view,
    // either using buttons or triggering it from outside with `setActiveMenu` custom event.
    useEffect(() => {
        if (!backButtonRef.current) return;
        if (!panelActive()) return;

        backButtonRef.current.focus();
    }, [visibility]);

    iimclient.setDisplayNameToPanel = setDisplayName;

    function toggleCallback(event?: React.MouseEvent<HTMLElement>) {
        if (event) {
            event.preventDefault();
        }
        augmentIdentitySilent();
        (window as any).NordicTracking.eventInternalFormOpened(
            false,
            (window as any).NordicTracking.PANELINFO_FUNCTION_NAME_PHONE
        );
        if (iimclient.callbackStartHook) {
            iimclient.callbackStartHook();
            return false;
        }
        const showCallback = !(visibility.activeMenu === 'wcb');
        setActiveMenu(showCallback ? 'wcb' : 'call');
        if (showCallback) {
            if (segment) {
                setCallMenuInitState(Date.now());
            }
        }
    }
    iimclient.toggleCallback = toggleCallback;

    const augmentIdentitySilent = () => {
        augmentIdentity().catch(() => {});
    };
    iimclient.augmentIdentitySilent = augmentIdentitySilent;

    const augmentIdentity = () => {
        const useIdentity = () => {
            if (iimclient.nickName) {
                setDisplayName(iimclient.nickName);
            }
            if (iimclient.mobile) {
                setMobile(iimclient.mobile);
            }
        };

        return new Promise<void>((resolve, reject) => {
            if (
                (window as any).ifhelp2015api &&
                !iimclient.isBusiness &&
                !iimclient.isBusinessChecked
            ) {
                const myBusinessUrl = (window as any).ifhelp2015api.getMyBusinessUrl();
                //console.log('isBusinessChecked is now true and will not be done again');
                iimclient.isBusiness = !!myBusinessUrl;
                iimclient.isBusinessChecked = true;
            }

            if (iimclient.isBusiness) {
                if ((window as any).Intelecom && iimclient) {
                    iimclient.chatName =
                        (window as any).Intelecom.personalName ||
                        (window as any).Intelecom.userName;
                    if ((window as any).Intelecom.emailAddress)
                        iimclient.email = (window as any).Intelecom.emailAddress;
                    (window as any).ifhelp2015api.saveIdentity({
                        nickName: iimclient.chatName,
                        waypointId: (window as any).Intelecom.waypointCustomerID,
                        email: iimclient.email,
                    });
                    useIdentity();
                }
                resolve();
                return;
            }

            (window as any).ifhelp2015api.getMypagesIdentity((error, identity) => {
                if (error) {
                    reject();
                    return;
                }
                (window as any).ifhelp2015api.saveIdentity(identity);
                useIdentity();
                resolve();
            });
        });
    };
    iimclient.augmentIdentity = augmentIdentity;

    const stopChatBannerTimers = () => {
        const chatClient = (window as any).vngage;
        if (chatClient && chatClient.stopBannerTimers) chatClient.stopBannerTimers();
    };

    const showMainMenu = () => {
        stopChatBannerTimers();
        setActiveMenu('main');

        tracking.eventPanelLoaded(tracking.PANELINFO_STATUS_EXPANDED);
    };

    const showChatMenu = () => {
        setActiveMenu('');
        setTooltipContent('');

        if ((window as any).ifhelp2015api.updateDEChatIdentity) {
            (window as any).ifhelp2015api.updateDEChatIdentity();
        }
    };

    const showCallMenu = () => {
        stopChatBannerTimers();
        setActiveMenu('call');

        tracking.eventFunctionIcon(false, tracking.PANELINFO_FUNCTION_NAME_PHONE);

        setTooltipContent('Phone window');
    };

    const showEmailMenu = () => {
        stopChatBannerTimers();
        setActiveMenu('email');

        tracking.eventFunctionIcon(false, tracking.PANELINFO_FUNCTION_NAME_EMAIL);

        setTooltipContent('Email window');
    };

    const closeMenu = () => {
        if (visibility.activeMenu === 'call' || visibility.activeMenu === 'wcb') {
            tracking.eventClosePanel(false, tracking.PANELINFO_FUNCTION_NAME_PHONE);
        }

        if (visibility.activeMenu === 'email') {
            tracking.eventClosePanel(false, tracking.PANELINFO_FUNCTION_NAME_EMAIL);
        }

        if (visibility.activeMenu === 'main') {
            tracking.eventClosePanel(false);
        }

        setActiveMenu('');
    };

    const toggleMenu = (menu: string) => {
        switch (menu) {
            case 'main':
                showMainMenu();
                break;
            case 'chat':
                showChatMenu();
                break;
            case 'call':
                showCallMenu();
                break;
            case 'email':
                showEmailMenu();
                break;
            case 'close':
                closeMenu();
                break;
        }
    };

    (window as any).showIfHelpMainMenu = () =>
        toggleMenu(visibility.activeMenu === 'main' ? 'close' : 'main');

    const clickBackgroundInMobile = (e) => {
        if (iimclient.isMobile && panelActive()) {
            if (e.target.id == 'ifhelp2015-wrapper') {
                toggleMenu('close');
            }
        }
    };

    const clickBackButton = () => {
        if (visibility.activeMenu === 'wcb') {
            toggleCallback();
        } else {
            toggleMenu('main');
        }
    };

    const clickCloseMenu = () => {
        toggleMenu('close');
    };

    const panelActive = () => {
        const activeMenu = visibility.activeMenu;
        return activeMenu === 'call' || activeMenu === 'wcb' || activeMenu === 'email';
    };

    const showAnyButton = () =>
        showChatButton(iimclient) || showCallButton(iimclient) || showEmailButton(iimclient);

    const showVillaWebshopIcon = () =>
        (window.location.href || '').includes('webshop.if.se/web/webshopprocess/if/villa') ||
        (window.location.hash || '').includes('villa');

    const showGuidanceIcon = () =>
        (window as any).vngage &&
        (window as any).vngage.isChatActive &&
        (window as any).vngage.isChatActive() &&
        (window as any).vngage.isGuidanceActive &&
        (window as any).vngage.isGuidanceActive();

    const guidanceIconSrc = (): string => {
        switch (iimclient.lang) {
            case 'lang_no':
                return 'images/guidance-continue-no.svg';
            case 'lang_sv':
            default:
                // Returning Swedish image as a fallback since there is no neutral or non country specific icon.
                return 'images/guidance-continue-sv.svg';
        }
    };

    const hidePanel =
        iimclient.hidePanel || isLoading || visibility.hidePanelReasons?.length || !showAnyButton();
    if (hidePanel) return null;

    return (
        <div
            id="ifhelp2015"
            className={`${iimclient.isMobile ? 'is-mobile ' : ''}${panelActive() ? 'active ' : ''}${
                iimclient.mobility ? 'mobility ' : ''
            }`.trim()}
            onClick={clickBackgroundInMobile}
        >
            <div id="ifhelp2015-wrapper" className="content">
                <div className="content-items">
                    {panelActive() && (
                        <div className="tooltip">
                            <div
                                id="cp-window"
                                className="tooltip-content"
                                aria-label={tooltipContent}
                            >
                                <div className="window-header">
                                    <div
                                        className="back-button"
                                        onClick={clickBackButton}
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ')
                                                clickBackButton();
                                        }}
                                        role="button"
                                        ref={backButtonRef}
                                    >
                                        <div className="back-button-text">
                                            {iimclient.texts['back-button']}
                                        </div>
                                    </div>
                                    <div
                                        className="tooltip-close"
                                        role="button"
                                        tabIndex={0}
                                        aria-label={iimclient.ariaLabels['close']}
                                        title={iimclient.texts['close']}
                                        onClick={clickCloseMenu}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ')
                                                clickBackButton();
                                        }}
                                    ></div>
                                </div>
                                <CallMenu
                                    iimclient={iimclient}
                                    api={(window as any).ifhelp2015api}
                                    initState={callMenuInitState}
                                    displayName={displayName}
                                    mobile={mobile}
                                    changeDisplayName={setDisplayName}
                                    changeMobile={setMobile}
                                    visibility={visibility}
                                />
                                <EmailMenu iimclient={iimclient} visibility={visibility} />
                            </div>
                        </div>
                    )}
                </div>
                {!visibility.activeMenu && (
                    <div className="content-icons" role="menu" aria-haspopup="true">
                        <div
                            className="action main-menu"
                            tabIndex={0}
                            role="menuitem"
                            aria-label={iimclient.ariaLabels['toggle-menu']}
                            onClick={() => toggleMenu('main')}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') toggleMenu('main');
                            }}
                        >
                            {showVillaWebshopIcon() ? (
                                <div className="chat-with-us-backplate">
                                    <IconChatWithUs
                                        frameWidth={174}
                                        frameHeight={44}
                                        width={132}
                                        height={24}
                                        color="#0054F0"
                                    />
                                </div>
                            ) : showGuidanceIcon() ? (
                                <div className="guidance-continue-backplate">
                                    <Svg src={guidanceIconSrc()} width={130} height={44} />
                                </div>
                            ) : (
                                <div className="menu-button-backplate">
                                    <IconChatBubbles
                                        frameWidth={64}
                                        frameHeight={64}
                                        width={32}
                                        height={29}
                                        color="#0054F0"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <div
                    className="content-buttons"
                    role="menu"
                    // Keep buttons always in the DOM so that external links work
                    style={{ display: visibility.activeMenu === 'main' ? '' : 'none' }}
                >
                    <ChatMenuButton iimclient={iimclient} toggleMenu={toggleMenu} />
                    <CallMenuButton iimclient={iimclient} toggleMenu={toggleMenu} />
                    <EmailMenuButton iimclient={iimclient} toggleMenu={toggleMenu} />
                    <CloseMenuButton iimclient={iimclient} toggleMenu={toggleMenu} />
                </div>
            </div>
        </div>
    );
};

export default IfHelpPanel;
